import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import {
  clearDeleteEmissionAttachement,
  clearListEmissionFiles,
  clearUploadEmissionAttachement,
  deleteEmissionAttachement,
  listEmissionFiles,
  uploadEmissionAttachement,
} from "../../../redux/actions";
import AttachmentCell from "./AttachmentCell";
import { STATUS } from "../../../redux/constants";
import useStyles from "./styles";
import CeroConfirmDrawer from "../../../components/CeroConfirmDrawer";
import { getCookie } from "../../../services/cookie";
import UploadIcon from "@mui/icons-material/Upload";

const ListEmissionFiles = ({ emissionId, isAuditor, company }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [showConfirm, setShowConfrim] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");

  const fileList = useSelector((state) => state.emission.listFiles.data);
  const uploadAttachementState = useSelector(
    (state) => state.emission.uploadAttachement
  );
  const deleteAttachementState = useSelector(
    (state) => state.emission.deleteAttachement
  );
  const readOnlyMode = getCookie("read_only_mode") === "disable_edit";

  useEffect(() => {
    dispatch(listEmissionFiles({ emissionId, isAuditor, company }));
    return () => {
      dispatch(clearListEmissionFiles);
      dispatch(clearUploadEmissionAttachement());
    };
  }, [dispatch]);

  useEffect(() => {
    if (uploadAttachementState.status === STATUS.SUCCESS) {
      enqueueSnackbar("Attachment added successfully", { variant: "success" });
      dispatch(listEmissionFiles({ emissionId, isAuditor, company }));
      dispatch(clearUploadEmissionAttachement());
    } else if (uploadAttachementState.status === STATUS.ERROR) {
      enqueueSnackbar(
        uploadAttachementState.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(clearUploadEmissionAttachement());
    }
  }, [uploadAttachementState]);

  useEffect(() => {
    if (deleteAttachementState.status === STATUS.SUCCESS) {
      enqueueSnackbar("Attachment deleted successfully", {
        variant: "success",
      });
      dispatch(listEmissionFiles({ emissionId, isAuditor, company }));
      setSelectedFile("");
      setShowConfrim(false);
      dispatch(clearDeleteEmissionAttachement());
    } else if (deleteAttachementState.status === STATUS.ERROR) {
      enqueueSnackbar(
        deleteAttachementState.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(clearDeleteEmissionAttachement());
    }
  }, [deleteAttachementState]);

  const onFileUpload = ({ target }) => {
    dispatch(uploadEmissionAttachement(emissionId, target.files[0]));
  };

  const onDeleteAttachement = (fileId) => {
    setSelectedFile(fileId);
    setShowConfrim(true);
  };

  const onConfirmDelete = () => {
    dispatch(deleteEmissionAttachement(emissionId, selectedFile));
  };

  const onCancelDelete = () => {
    setSelectedFile("");
    setShowConfrim(false);
  };

  return (
    <Box className={classes.listContainer}>
      <Box>
        <Button
          variant="contained"
          component="label"
          className={classes.uploadButton}
          disabled={isAuditor || readOnlyMode}
        >
          {uploadAttachementState.status === STATUS.RUNNING ? (
            <>
              <RefreshIcon /> Uploading
            </>
          ) : (
            <>
              <UploadIcon /> Upload a file
              <input type="file" hidden onChange={onFileUpload} />
            </>
          )}
        </Button>
      </Box>
      <Box className={classes.fileListContainer}>
        {fileList.map((file, index) => (
          <AttachmentCell
            key={file.id}
            url={file.blob_url}
            imageName={file.file_name}
            imageType={file.content_type}
            attachmentId={file.id}
            onDeleteAttachment={onDeleteAttachement}
          />
        ))}
        {!fileList.length && (
          <Typography variant="h7" component="span">
            {" "}
            No Attachment found{" "}
          </Typography>
        )}
      </Box>
      {showConfirm && (
        <CeroConfirmDrawer
          isOpen={showConfirm}
          onClose={onCancelDelete}
          onConfirm={onConfirmDelete}
        />
      )}
    </Box>
  );
};

export default ListEmissionFiles;
